import { useEffect, useState } from "react";

/**
 * Format a duration in seconds as HH:MM:SS (or Nd HH:MM:SS if > 24h).
 */
export function formatElapsed(totalSeconds) {
  if (totalSeconds == null || isNaN(totalSeconds) || totalSeconds < 0) return "—";
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const base = `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return days > 0 ? `${days}d ${base}` : base;
}

/**
 * Format the same seconds as human "2h 14m 06s" — used at the end of a
 * completed campaign as the final total-time headline.
 */
export function formatHuman(totalSeconds) {
  if (totalSeconds == null || isNaN(totalSeconds) || totalSeconds < 0) return "—";
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  if (mins || hours || days) parts.push(`${mins}m`);
  parts.push(`${secs}s`);
  return parts.join(" ");
}

/**
 * Live-ticking hook for a campaign's elapsed time.
 *
 * The timer starts from `started_at` and freezes when the campaign is
 * `completed`/`stopped` (uses `completed_at`/`stopped_at` respectively).
 * It KEEPS TICKING during batch pauses and user pauses — the user
 * explicitly wants a single wall-clock timer that only stops on final
 * termination, not on every pause.
 *
 * Returns `{ seconds, running, endReason }`.
 */
export function useCampaignElapsed(campaign) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const running = campaign && campaign.status !== "completed"
      && campaign.status !== "stopped" && campaign.status !== "draft"
      && !!campaign.started_at;
    if (!running) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [campaign?.status, campaign?.started_at]);

  if (!campaign || !campaign.started_at) {
    return { seconds: null, running: false, endReason: null };
  }
  const start = new Date(campaign.started_at).getTime();
  let end;
  let endReason = null;
  if (campaign.status === "completed" && campaign.completed_at) {
    end = new Date(campaign.completed_at).getTime();
    endReason = "completed";
  } else if (campaign.status === "stopped" && campaign.stopped_at) {
    end = new Date(campaign.stopped_at).getTime();
    endReason = "stopped";
  } else {
    end = now;
  }
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  return { seconds, running: endReason === null, endReason };
}
